import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import { SESSION_OPTIONS } from "../../constants";
import { parseSessions, parseTrainers } from "../../utils/multivalue";
import {
  Button,
  cx,
  DataCard,
  Empty,
  Field,
  Icon,
  Input,
  Modal,
  PageTitle,
  Select,
  Spinner,
  Td,
  Th
} from "../../components/ui";

const DAY_OPTIONS = [
  { key: "Mon", label: "Sen" },
  { key: "Tue", label: "Sel" },
  { key: "Wed", label: "Rab" },
  { key: "Thu", label: "Kam" },
  { key: "Fri", label: "Jum" },
  { key: "Sat", label: "Sab" },
  { key: "Sun", label: "Min" },
];

function parseScheduleToDays(schedStr) {
  if (!schedStr) return [];
  const s = String(schedStr).trim().toLowerCase().replace(/\s+/g, "").replace(/[–—]/g, "-");
  if (s.includes("everyday") || s.includes("setiaphari") || s.includes("setiap-hari")) {
    return ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  }
  if (s.includes("mon-fri") || s.includes("sen-jum") || s === "5x") {
    return ["Mon", "Tue", "Wed", "Thu", "Fri"];
  }
  const aliasMap = {
    Mon: ["mon", "senin", "sen"],
    Tue: ["tue", "selasa", "sel"],
    Wed: ["wed", "rabu", "rab"],
    Thu: ["thu", "kamis", "kam"],
    Fri: ["fri", "jumat", "jum"],
    Sat: ["sat", "sabtu", "sab"],
    Sun: ["sun", "minggu", "min"],
  };
  const parts = s.split("-");
  return Object.entries(aliasMap)
    .filter(([, aliases]) => aliases.some((a) => parts.includes(a)))
    .map(([key]) => key);
}

function formatDaysToSchedule(days) {
  const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const sorted = order.filter((d) => days.includes(d));
  if (sorted.length === 0) return "";
  if (sorted.length === 7) return "Everyday";
  if (sorted.length === 5 && !sorted.includes("Sat") && !sorted.includes("Sun")) return "Mon-Fri";
  return sorted.join("-");
}

function parsePeriodeCount(periode) {
  if (!periode) return null;
  const match = String(periode).trim().match(/^(\d+)x$/i);
  return match ? parseInt(match[1], 10) : null;
}

// Label jadwal ringkas & terbaca dari string schedule (mis. "Sen · Rab · Jum").
function scheduleLabel(schedule) {
  const days = parseScheduleToDays(schedule);
  if (!days.length) return "-";
  if (days.length === 7) return "Setiap hari";
  const abbr = Object.fromEntries(DAY_OPTIONS.map((d) => [d.key, d.label]));
  return days.map((d) => abbr[d] || d).join(" · ");
}

const emptyStudent = {
  studentId: "",
  name: "",
  // Turunan dari kelas — ditampilkan, tak pernah diketik. Lihat ClassPicker.
  trainer: "",
  sessions: "",
  // Yang benar-benar dikirim saat menambah murid: id kelas, bukan ejaan namanya.
  classIds: [],
  periode: "",
  schedule: "",
  wa: "",
  email: "",
  status: "aktif"
};

// Status murid: 'aktif' tampil di absensi; 'off'/'cuti' DISEMBUNYIKAN dari daftar absensi
// (tapi tetap ada di daftar ini & riwayat recap/gaji tak berubah).
const STATUS_META = {
  aktif: { label: "Aktif", cls: "bg-green-500/15 text-green-600 dark:text-green-400" },
  off: { label: "Off", cls: "bg-red-500/15 text-red-600 dark:text-red-400" },
  cuti: { label: "Cuti", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" }
};
const STATUS_KEYS = ["aktif", "off", "cuti"];

export default function AdminStudentsPage({ setToast, activeBranch = null }) {
  const [rows, setRows] = useState([]);
  const [classes, setClasses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  // null = tertutup; {} = tambah baru; objek murid = edit.
  const [editing, setEditing] = useState(null);

  const branchById = useMemo(() => new Map(branches.map((b) => [b.id, b])), [branches]);
  // Tampilkan kolom/chip cabang hanya saat data mencakup >1 cabang (mode "Semua").
  const showBranch = useMemo(
    () => new Set(rows.map((r) => r.branchId)).size > 1,
    [rows]
  );

  const [filters, setFilters] = useState({
    q: "",
    trainer: "All",
    session: "All",
    status: "All"
  });

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  async function load() {
    setLoading(true);

    try {
      // Daftar kelas dipakai form "Tambah Murid". Endpoint-nya butuh hak kelola jadwal
      // cabang; admin punya, tapi kalau suatu saat ditolak jangan sampai seluruh halaman
      // ikut gagal — formnya cukup memberi tahu dan tetap bisa menyimpan data orangnya.
      // Daftar nickname trainer tak lagi dimuat: sejak Trainer & Sesi jadi kolom turunan,
      // tak ada lagi yang mengetiknya di halaman ini. Yang dibutuhkan form kini daftar KELAS.
      const [studentsRes, branchesRes, kelasRes] = await Promise.all([
        api.getAllStudents(),
        api.getBranches(),
        api.getClasses().catch(() => ({ success: false, data: [] }))
      ]);

      setClasses(kelasRes?.success ? (kelasRes.data || []) : []);

      if (studentsRes.success) {
        setRows(studentsRes.data || []);
      }

      if (branchesRes?.success) {
        setBranches(branchesRes.data || []);
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Failed to load students"
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const trainerValues = useMemo(() => {
    return Array.from(new Set(rows.map((r) => r.trainer).filter(Boolean)));
  }, [rows]);

  const visible = rows.filter((student) => {
    const q = [student.name, student.studentId]
      .join(" ")
      .toLowerCase()
      .includes(filters.q.toLowerCase());

    return (
      q &&
      (filters.trainer === "All" ||
        parseTrainers(student.trainer).includes(filters.trainer)) &&
      (filters.session === "All" ||
        parseSessions(student.sessions).includes(filters.session)) &&
      (filters.status === "All" || (student.status || "aktif") === filters.status)
    );
  });

  // Reset ke halaman 1 setiap kali filter berubah
  useEffect(() => {
    setPage(1);
  }, [filters.q, filters.trainer, filters.session, filters.status]);

  // Ubah status murid cepat (inline) — 'off'/'cuti' menyembunyikan dari daftar absensi.
  async function setStatus(student, value) {
    setRows((rs) =>
      rs.map((r) => (r.rowNumber === student.rowNumber ? { ...r, status: value } : r))
    );
    try {
      const res = await api.updateStudentInline(student.rowNumber, "status", value);
      if (!res.success) throw new Error(res.message || "Gagal");
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal mengubah status" });
      load(); // pulihkan dari server bila gagal
    }
  }

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Simpan: update bila ada rowNumber (edit), else tambah baru.
  async function save(payload) {
    try {
      const res = payload.rowNumber
        ? await api.updateStudent(payload.rowNumber, payload)
        : await api.addStudent(payload);

      setToast({
        type: res.success ? "success" : "error",
        message: res.message || "Tersimpan"
      });

      if (res.success) {
        setEditing(null);
        load();
      }
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Gagal menyimpan"
      });
    }
  }

  async function del(rowNumber) {
    if (!window.confirm("Delete this student?")) return;

    try {
      const res = await api.deleteStudent(rowNumber);

      setToast({
        type: res.success ? "success" : "error",
        message: res.message || "Done"
      });

      if (res.success) load();
    } catch (err) {
      setToast({
        type: "error",
        message: err.message || "Delete failed"
      });
    }
  }

  return (
    <>
      <PageTitle
        title="Global Student List"
        subtitle="Create, edit, filter, and delete student records from MySQL."
        action={
          <div className="flex flex-wrap gap-2">
            <Input
            placeholder="Search name/ID..."
            value={filters.q}
            onChange={(e) =>
                setFilters({
                ...filters,
                q: e.target.value
                })
            }
            className="w-full sm:w-56"
            />

            <Select
              value={filters.trainer}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  trainer: e.target.value
                })
              }
              className="w-40"
            >
              <option>All</option>
              {trainerValues.map((trainer) => (
                <option key={trainer}>{trainer}</option>
              ))}
            </Select>

            <Select
              value={filters.session}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  session: e.target.value
                })
              }
              className="w-40"
            >
              <option>All</option>
              {SESSION_OPTIONS.map((session) => (
                <option key={session}>{session}</option>
              ))}
            </Select>

            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-32"
            >
              <option value="All">Semua status</option>
              {STATUS_KEYS.map((k) => (
                <option key={k} value={k}>{STATUS_META[k].label}</option>
              ))}
            </Select>

            <Button onClick={() => setEditing({})}>
              <Icon name="person_add" />
              Tambah Murid
            </Button>
          </div>
        }
      />

      {loading ? (
        <Spinner />
      ) : (
        <>
        {/* Desktop: tabel */}
        <DataCard className="hidden md:block">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[840px] whitespace-nowrap text-left">
            <thead>
              <tr>
                {[
                  "ID",
                  "NAME",
                  "TRAINER",
                  "PERIODE",
                  "SESSIONS",
                  "SCHEDULE",
                  "WA",
                  "EMAIL",
                  "STATUS",
                  "AKSI"
                ].map((h) => (
                  <Th key={h} right={h === "AKSI"}>
                    {h}
                  </Th>
                ))}
              </tr>
            </thead>

            <tbody>
              {paged.map((student) => (
                <tr
                  key={student.rowNumber}
                  className="border-t border-slate-200 dark:border-slate-800"
                >
                  <Td mono primary>
                    {student.studentId}
                    {showBranch && (
                      <span className="ml-2 align-middle rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-slate-800">
                        {branchById.get(student.branchId)?.code || `#${student.branchId}`}
                      </span>
                    )}
                  </Td>

                  <Td>
                    <span className="font-black">{student.name || "-"}</span>
                  </Td>
                  <Td>{student.trainer || "-"}</Td>
                  <Td>{student.periode || "-"}</Td>
                  <Td>{student.sessions || "-"}</Td>
                  <Td>
                    <span className="text-xs font-bold text-slate-500">
                      {scheduleLabel(student.schedule)}
                    </span>
                  </Td>
                  <Td>{student.wa || "-"}</Td>
                  <Td>
                    <span className="block max-w-[200px] truncate" title={student.email || ""}>
                      {student.email || "-"}
                    </span>
                  </Td>

                  <Td>
                    <StatusSelect
                      value={student.status}
                      onChange={(v) => setStatus(student, v)}
                    />
                  </Td>

                  <Td right>
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => setEditing(student)}>
                        <Icon name="edit" />
                        Edit
                      </Button>
                      <Button variant="danger" onClick={() => del(student.rowNumber)}>
                        <Icon name="delete" />
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}

              {!visible.length && (
                <tr>
                  <Td colSpan="10">No students found.</Td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </DataCard>

        {/* Mobile: kartu per-siswa (nama & field mudah dibaca) */}
        <div className="space-y-3 md:hidden">
          {paged.map((student) => (
            <StudentCard
              key={student.rowNumber}
              student={student}
              branchLabel={showBranch ? (branchById.get(student.branchId)?.code || `#${student.branchId}`) : null}
              onEdit={setEditing}
              onDelete={del}
              onStatus={setStatus}
            />
          ))}
          {!visible.length && <Empty label="No students found." />}
        </div>

        {/* Paginasi (dipakai desktop & mobile) */}
        {visible.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900/30">
            <p className="text-xs text-slate-500">
              Menampilkan {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, visible.length)} dari{" "}
              {visible.length} siswa
            </p>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300"
              >
                ← Prev
              </button>

              <span className="text-xs font-black">
                Hal {currentPage} / {totalPages}
              </span>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300"
              >
                Next →
              </button>
            </div>
          </div>
        )}
        </>
      )}

      <StudentModal
        open={!!editing}
        student={editing || {}}
        classes={classes}
        branchCode={activeBranch?.code || ""}
        onClose={() => setEditing(null)}
        onSave={save}
        setToast={setToast}
        // ID sudah diganti di server: daftar dimuat ulang, form Edit tetap terbuka
        // dengan isiannya yang belum disimpan.
        onIdBerubah={load}
      />
    </>
  );
}

// Dropdown status berwarna (dipakai di tabel & kartu). Ubah 'off'/'cuti' menyembunyikan
// murid dari daftar absensi.
function StatusSelect({ value, onChange }) {
  const v = (value || "aktif").toLowerCase();
  const meta = STATUS_META[v] || STATUS_META.aktif;
  return (
    <select
      value={v}
      onChange={(e) => onChange(e.target.value)}
      title="Status murid"
      className={cx(
        "cursor-pointer rounded-lg border-0 px-2 py-1 text-xs font-black outline-none focus:ring-2 focus:ring-primary",
        meta.cls
      )}
    >
      {STATUS_KEYS.map((k) => (
        <option
          key={k}
          value={k}
          className="bg-white font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200"
        >
          {STATUS_META[k].label}
        </option>
      ))}
    </select>
  );
}

function StudentCard({ student, branchLabel, onEdit, onDelete, onStatus }) {
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/40">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs font-black text-primary">{student.studentId}</span>
            {branchLabel && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500 dark:bg-slate-800">
                {branchLabel}
              </span>
            )}
            <StatusSelect value={student.status} onChange={(v) => onStatus(student, v)} />
          </span>
          <p className="mt-1 text-lg font-black">{student.name || "-"}</p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="secondary" onClick={() => onEdit(student)} className="px-2.5 py-1.5">
            <Icon name="edit" />
          </Button>
          <Button variant="danger" onClick={() => onDelete(student.rowNumber)} className="px-2.5 py-1.5">
            <Icon name="delete" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <CardField label="Trainer" value={student.trainer} />
        <CardField label="Periode" value={student.periode} />
        <CardField label="Sesi" value={student.sessions} />
        <CardField label="Schedule" value={scheduleLabel(student.schedule)} />
        <CardField label="WhatsApp" value={student.wa} />
        <CardField label="Email" value={student.email} />
      </div>
    </div>
  );
}

function CardField({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="truncate text-sm font-bold" title={value || ""}>{value || "-"}</p>
    </div>
  );
}

function DayPicker({ value, onChange, periode }) {
  const [selected, setSelected] = useState(() => parseScheduleToDays(value));

  useEffect(() => {
    setSelected(parseScheduleToDays(value));
  }, [value]);

  const expectedCount = parsePeriodeCount(periode);
  const isMatch = expectedCount === null || selected.length === expectedCount;

  function toggle(day) {
    const next = selected.includes(day)
      ? selected.filter((d) => d !== day)
      : [...selected, day];
    setSelected(next);
    onChange(formatDaysToSchedule(next));
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {DAY_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => toggle(key)}
            className={cx(
              "rounded-lg px-2.5 py-1.5 text-xs font-black border transition select-none",
              selected.includes(key)
                ? "bg-primary text-white border-primary"
                : "border-slate-200 bg-white text-slate-500 hover:border-primary hover:text-primary dark:bg-slate-800 dark:border-slate-700"
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <p className={cx(
        "text-xs font-bold",
        selected.length === 0
          ? "text-slate-400"
          : isMatch
          ? "text-green-600 dark:text-green-400"
          : "text-amber-500 dark:text-amber-400"
      )}>
        {selected.length === 0
          ? "Belum ada hari dipilih"
          : isMatch
          ? `✓ ${selected.length} hari dipilih · Sesuai ${periode}`
          : `⚠ ${selected.length} hari dipilih · Tidak sesuai ${periode} (seharusnya ${expectedCount} hari)`
        }
        {!periode && selected.length > 0 && `${selected.length} hari dipilih`}
      </p>
    </div>
  );
}

// PEMILIH KELAS — pengganti kotak ketik Trainer & Sesi.
//
// Dulu admin mengetik nama trainer dan sesi di sini, lalu backend memakai TEKS itu untuk
// mencari kelasnya. Di situlah akar seluruh keluarga bug proyek ini: "Mr.Ihya" vs
// "Mr. Ihya", "08:30" vs "08.30", murid mendarat di cabang yang salah. Sekarang yang
// dikirim adalah `class_id` — tak ada ejaan yang bisa meleset — dan Trainer & Sesi
// murid DITURUNKAN dari kelas itu oleh backend.
//
// Kelas dibuat & diubah di Manajemen Kelas. Halaman ini hanya memilih dari yang ada,
// supaya tak ada dua tempat yang bisa melahirkan kelas.
function ClassPicker({ value, classes, onChange }) {
  const dipilih = Array.isArray(value) ? value.map(Number) : [];
  const tersedia = (classes || []).filter((k) => k.isActive !== false);
  const cari = (id) => tersedia.find((k) => Number(k.id) === Number(id));
  const label = (k) =>
    `${k.session || "(sesi kosong)"} · ${k.trainerText || "(belum ada trainer)"}`;

  function tambah(id) {
    const n = Number(id);
    if (!n || dipilih.includes(n)) return;
    onChange([...dipilih, n]);
  }
  function hapus(id) {
    onChange(dipilih.filter((x) => Number(x) !== Number(id)));
  }

  if (!tersedia.length) {
    return (
      <p className="text-xs text-amber-600 dark:text-amber-400">
        Belum ada kelas aktif di cabang ini. Buat kelasnya dulu di <b>Manajemen Kelas</b>,
        baru muridnya bisa ditempatkan.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <Select
        value=""
        onChange={(e) => {
          tambah(e.target.value);
          e.target.value = "";
        }}
      >
        <option value="">+ Tambah kelas</option>
        {tersedia
          .filter((k) => !dipilih.includes(Number(k.id)))
          .map((k) => (
            <option key={k.id} value={k.id}>
              {label(k)}
            </option>
          ))}
      </Select>
      {dipilih.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {dipilih.map((id) => {
            const k = cari(id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2 py-1 text-xs font-bold text-primary"
              >
                {k ? label(k) : `Kelas #${id}`}
                <button
                  type="button"
                  onClick={() => hapus(id)}
                  className="text-primary/60 hover:text-red-500"
                  title="Hapus"
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      <p className="text-xs text-slate-400">
        Trainer &amp; Sesi murid akan mengikuti kelas yang dipilih di sini.
      </p>
    </div>
  );
}

// Nilai turunan yang hanya ditampilkan. Sengaja bukan input: satu-satunya penulisnya
// adalah perubahan keanggotaan kelas.
function Turunan({ nilai, kosong }) {
  return (
    <div>
      <p className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
        {nilai || kosong}
      </p>
      <p className="mt-1 text-xs text-slate-400">Mengikuti kelas murid — ubah di Manajemen Kelas.</p>
    </div>
  );
}

function StudentModal({
  open,
  student,
  classes = [],
  branchCode = "",
  onClose,
  onSave,
  setToast = () => {},
  onIdBerubah = () => {}
}) {
  const [form, setForm] = useState(emptyStudent);
  const [gantiId, setGantiId] = useState(false);
  const isEdit = !!form.rowNumber;
  const awalan = branchCode ? `${branchCode}-` : "";
  const sisaId = lepasAwalan(form.studentId, awalan);

  useEffect(() => {
    if (!open) return;
    const awal = { ...emptyStudent, ...(student || {}) };
    // Murid BARU: ID langsung diawali kode cabang aktif, admin tinggal mengetik
    // lanjutannya. Awalannya diambil dari kode cabang yang sedang aktif \u2014 sumber yang
    // sama dengan yang dipakai migrasi awalan, supaya tak ada dua aturan yang berselisih.
    // Awalannya TERKUNCI (keputusan 2 Okt 2026): murid pindahan pun memakai awalan
    // cabang barunya, dan server menolak ID tanpa awalan cabangnya.
    if (!awal.rowNumber && !awal.studentId && branchCode) awal.studentId = `${branchCode}-`;
    setForm(awal);
    setGantiId(false);
  }, [open, student, branchCode]);

  function patch(key, value) {
    setForm({
      ...form,
      [key]: value
    });
  }

  return (
    <Modal title={isEdit ? "Edit Murid" : "Tambah Murid"} open={open} onClose={onClose} wide>
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Student ID">
          {isEdit ? (
            // ID tak diketik di sini: menggantinya memindahkan SEMUA catatan murid
            // (absensi, kelas, dll), jadi lewat dialognya sendiri yang menunjukkan
            // apa saja yang ikut pindah sebelum disimpan.
            <div className="flex gap-2">
              <Input value={form.studentId} disabled className="min-w-0 flex-1" />
              <Button variant="secondary" type="button" onClick={() => setGantiId(true)}>
                <Icon name="edit" className="text-base" />
                Ganti ID
              </Button>
            </div>
          ) : awalan ? (
            <IdBerawalan
              awalan={awalan}
              value={sisaId}
              onChange={(sisa) => patch("studentId", awalan + sisa)}
              autoFocus
              label={`Lanjutan ID murid setelah ${awalan}`}
            />
          ) : (
            <Input
              value={form.studentId}
              onChange={(e) => patch("studentId", e.target.value)}
              placeholder="STD001"
              autoFocus
            />
          )}
          {isEdit ? (
            <p className="mt-1 text-xs text-slate-400">
              Semua catatan murid ini ikut pindah ke ID barunya.
            </p>
          ) : awalan ? (
            <p className={cx("mt-1 text-xs", /\s/.test(sisaId) ? "text-amber-600 dark:text-amber-400" : "text-slate-400")}>
              {/\s/.test(sisaId) ? (
                "ID murid tidak boleh mengandung spasi."
              ) : (
                <>
                  Awalan <b>{awalan}</b> sudah terkunci, tinggal ketik lanjutannya. Murid
                  pindahan pun memakai awalan cabang ini.
                </>
              )}
            </p>
          ) : (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              Pilih cabang dulu di pojok atas, supaya ID-nya otomatis berawalan kode cabang
              dan muridnya masuk ke cabang yang benar.
            </p>
          )}
        </Field>

        <Field label="Student Name">
          <Input
            value={form.name}
            onChange={(e) => patch("name", e.target.value)}
            placeholder="Student full name"
          />
        </Field>

        <Field label={isEdit ? "Trainer" : "Kelas"}>
          {isEdit ? (
            <Turunan nilai={form.trainer} kosong="Belum masuk kelas mana pun" />
          ) : (
            <ClassPicker
              value={form.classIds}
              classes={classes}
              onChange={(v) => patch("classIds", v)}
            />
          )}
        </Field>

        <Field label="Periode">
          <Input
            value={form.periode}
            onChange={(e) => patch("periode", e.target.value)}
            placeholder="5x / 3x / etc"
          />
        </Field>

        <Field label="Sesi">
          <Turunan
            nilai={form.sessions}
            kosong={isEdit ? "Belum masuk kelas mana pun" : "Mengikuti kelas yang dipilih"}
          />
        </Field>

        <Field label="Schedule">
          <DayPicker
            value={form.schedule}
            onChange={(v) => patch("schedule", v)}
            periode={form.periode}
          />
        </Field>

        <Field label="WhatsApp">
          <Input
            value={form.wa}
            onChange={(e) => patch("wa", e.target.value)}
            placeholder="08xxxxxxxxxx"
          />
        </Field>

        <Field label="Email">
          <Input
            type="email"
            value={form.email}
            onChange={(e) => patch("email", e.target.value)}
            placeholder="student@email.com"
          />
        </Field>

        <Field label="Status (off/cuti disembunyikan dari absensi)">
          <Select
            value={(form.status || "aktif").toLowerCase()}
            onChange={(e) => patch("status", e.target.value)}
          >
            {STATUS_KEYS.map((k) => (
              <option key={k} value={k}>{STATUS_META[k].label}</option>
            ))}
          </Select>
        </Field>
      </div>

      <Button className="mt-5 w-full" onClick={() => onSave(form)}>
        {isEdit ? "Simpan Perubahan" : "Simpan Murid"}
      </Button>

      {gantiId && isEdit && (
        <GantiIdModal
          student={form}
          setToast={setToast}
          onClose={() => setGantiId(false)}
          onDone={(baru) => {
            // Hanya ID-nya yang diperbarui di form — isian lain yang belum disimpan
            // (nama, WA, dll) tetap utuh.
            patch("studentId", baru);
            setGantiId(false);
            onIdBerubah();
          }}
        />
      )}
    </Modal>
  );
}

// ── Kolom ID berawalan kode cabang yang TERKUNCI ──────────────────────────────
// Awalannya bukan bagian dari yang diketik, jadi mustahil terhapus. Kalau admin
// menempelkan ID lengkap ("BR01-3A26"), awalan gandanya dibuang.
export function lepasAwalan(teks, awalan) {
  const t = String(teks || "");
  return awalan && t.toLowerCase().startsWith(awalan.toLowerCase()) ? t.slice(awalan.length) : t;
}

export function IdBerawalan({ awalan, value, onChange, autoFocus = false, disabled = false, label }) {
  return (
    <div
      className={cx(
        "flex items-stretch overflow-hidden rounded-lg border border-slate-200 bg-slate-50 focus-within:ring-2 focus-within:ring-primary dark:border-slate-700 dark:bg-slate-800",
        disabled && "opacity-60"
      )}
    >
      <span className="flex select-none items-center border-r border-slate-200 bg-slate-100 px-3 font-mono text-sm font-black text-slate-500 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-400">
        {awalan}
      </span>
      <input
        value={value}
        onChange={(e) => onChange(lepasAwalan(e.target.value, awalan))}
        placeholder="3A26"
        aria-label={label}
        autoFocus={autoFocus}
        disabled={disabled}
        className="min-w-0 flex-1 bg-transparent px-3 py-2 font-mono text-sm outline-none dark:text-white"
      />
    </div>
  );
}

// ── Dialog Ganti ID ───────────────────────────────────────────────────────────
// Menunjukkan apa yang ikut pindah, memeriksa ID baru saat diketik (pemeriksaan
// yang sama dengan yang dipakai server saat menyimpan), lalu baru boleh disimpan.
const CATATAN_ID = [
  ["absensi", "how_to_reg", "rekap absensi (per bulan & sesi)"],
  ["jamDatang", "schedule", "jam datang"],
  ["kelas", "school", "kelas"],
  ["tambahan", "person_add", "murid tambahan"],
  ["reschedule", "event_repeat", "reschedule"],
  ["raportNilai", "grading", "nilai raport"],
  ["raportKomentar", "chat", "komentar raport"],
  ["raportKelasUtama", "push_pin", "kelas utama raport"],
  ["raportTerbit", "description", "raport terbit"],
  ["salinanReset", "restart_alt", "salinan reset absensi"]
];

function waktuSingkat(v) {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

export function GantiIdModal({ student, onClose, onDone, setToast }) {
  const [info, setInfo] = useState(null);
  const [memuat, setMemuat] = useState(true);
  const [sisa, setSisa] = useState("");
  const [cek, setCek] = useState({ untuk: "", boleh: false, alasan: "", memeriksa: false });
  const [kirim, setKirim] = useState(false);
  const urutan = useRef(0);

  // Pratinjau awal: ID sekarang, awalan wajib, dan berapa catatan yang ikut pindah.
  useEffect(() => {
    let batal = false;
    (async () => {
      try {
        const r = await api.previewChangeStudentId(student.rowNumber);
        if (batal) return;
        if (!r?.success) throw new Error(r?.message || "Gagal memuat data murid.");
        setInfo(r);
        // Usulan awal = ID sekarang tanpa awalan & spasi. Untuk ID lama tanpa awalan
        // (mis. "3A26"), itu langsung menjadi "BR01-3A26" — perbaikan sekali klik.
        setSisa(lepasAwalan(String(r.lama || ""), r.awalan || "").replace(/\s+/g, ""));
      } catch (err) {
        if (batal) return;
        setToast({ type: "error", message: err.message || "Gagal memuat data murid." });
        onClose();
      } finally {
        if (!batal) setMemuat(false);
      }
    })();
    return () => {
      batal = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const awalan = info?.awalan || "";
  const idBaru = awalan + sisa;
  // Tanpa ID baru pun server sudah menjawab "belum aktif" bila migrasinya belum jalan.
  const tidakAktif = !!info?.alasan;
  const samaDenganSekarang = !!info && idBaru === info.lama;

  // Periksa ID baru saat diketik — ditunda sebentar supaya tak tiap huruf, dan jawaban
  // yang datang terlambat untuk ketikan lama diabaikan.
  useEffect(() => {
    if (!info || tidakAktif) return undefined;
    if (!sisa || samaDenganSekarang) {
      urutan.current += 1;
      setCek({ untuk: "", boleh: false, alasan: "", memeriksa: false });
      return undefined;
    }
    const nomor = ++urutan.current;
    setCek((c) => ({ ...c, memeriksa: true }));
    const t = setTimeout(async () => {
      try {
        const r = await api.previewChangeStudentId(student.rowNumber, idBaru);
        if (nomor !== urutan.current) return;
        setCek({ untuk: idBaru, boleh: !!r?.boleh, alasan: r?.alasan || "", memeriksa: false });
      } catch (err) {
        if (nomor !== urutan.current) return;
        setCek({ untuk: idBaru, boleh: false, alasan: err.message || "Gagal memeriksa.", memeriksa: false });
      }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sisa, info]);

  const siap = !kirim && !cek.memeriksa && cek.boleh && cek.untuk === idBaru;

  async function simpan() {
    if (!siap) return;
    setKirim(true);
    try {
      const r = await api.changeStudentId(student.rowNumber, idBaru);
      setToast({ type: r?.success ? "success" : "error", message: r?.message || "Selesai." });
      if (r?.success) onDone(r.baru || idBaru);
      else setKirim(false);
    } catch (err) {
      setToast({ type: "error", message: err.message || "Gagal mengganti ID." });
      setCek({ untuk: idBaru, boleh: false, alasan: err.message || "", memeriksa: false });
      setKirim(false);
    }
  }

  const h = info?.hitungan || {};
  const baris = CATATAN_ID.filter(([k]) => Number(h[k]) > 0);
  const riwayat = info?.riwayat || [];

  return (
    <Modal title="Ganti ID Murid" open onClose={onClose}>
      <div className="space-y-4 text-sm">
        <p className="text-slate-500">
          <span className="font-bold text-slate-700 dark:text-slate-200">
            {info?.nama || student.name}
          </span>
          {" · ID sekarang "}
          <span className="font-mono font-black text-slate-700 dark:text-slate-200">
            {info?.lama || student.studentId}
          </span>
        </p>

        {memuat ? (
          <Spinner label="Memeriksa catatan murid ini…" />
        ) : tidakAktif ? (
          <div className="rounded-xl bg-amber-500/10 p-3 text-xs font-bold text-amber-700 dark:text-amber-400">
            {info.alasan}
          </div>
        ) : (
          <>
            <div>
              <p className="mb-1 text-xs font-black uppercase tracking-wider text-slate-500">ID baru</p>
              <IdBerawalan
                awalan={awalan}
                value={sisa}
                onChange={setSisa}
                autoFocus
                label={`Lanjutan ID baru setelah ${awalan}`}
              />
              <p
                role="status"
                className={cx(
                  "mt-1 min-h-[1.25rem] text-xs font-bold",
                  cek.boleh && cek.untuk === idBaru
                    ? "text-green-600 dark:text-green-400"
                    : cek.alasan
                    ? "text-red-600 dark:text-red-400"
                    : "text-slate-400"
                )}
              >
                {cek.memeriksa
                  ? "Memeriksa…"
                  : samaDenganSekarang
                  ? "Ketik ID barunya."
                  : cek.boleh && cek.untuk === idBaru
                  ? `${idBaru} bisa dipakai.`
                  : cek.alasan}
              </p>
            </div>

            <div>
              <p className="mb-2 text-xs font-black uppercase tracking-wider text-slate-500">
                Ikut pindah ke ID baru
              </p>
              {baris.length ? (
                <ul className="space-y-1.5">
                  {baris.map(([k, ikon, teks]) => (
                    <li key={k} className="flex items-center gap-2">
                      <Icon name={ikon} className="text-base text-primary" />
                      <span className="font-black tabular-nums">{h[k]}</span>
                      <span className="text-slate-600 dark:text-slate-300">{teks}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">Belum ada catatan apa pun — hanya data muridnya.</p>
              )}
            </div>

            {riwayat.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-black uppercase tracking-wider text-slate-500">
                  ID sebelumnya
                </p>
                <ul className="space-y-0.5 text-xs text-slate-500">
                  {riwayat.map((x, i) => (
                    <li key={i}>
                      <span className="font-mono">{x.lama}</span> → <span className="font-mono">{x.baru}</span>
                      {x.oleh ? ` · ${x.oleh}` : ""}
                      {x.pada ? ` · ${waktuSingkat(x.pada)}` : ""}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-xs text-slate-500">
              Excel atau slip yang sudah diunduh tetap memakai ID lama. Setiap penggantian
              tercatat, jadi ID lama tetap bisa ditelusuri.
            </p>
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={kirim}>
            Batal
          </Button>
          {!memuat && !tidakAktif && (
            <Button onClick={simpan} disabled={!siap}>
              {kirim ? "Mengganti…" : "Ganti ID"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}